import React, { useState, useEffect } from 'react';
import { 
  Clock,
  FileImage,
  FileVideo,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ArrowRight,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { RecentAnalysesService, RecentAnalysisItem } from '../../lib/recent-analyses';
import { truncateAnalysisName, formatRelativeDate } from '../../lib/utils';
import { SecureImage } from '../SecureImage';

interface RecentAnalysesWidgetProps {
  onViewAll?: () => void;
  onHistoryClick?: (analysis: RecentAnalysisItem) => void;
  className?: string;
  refreshTrigger?: number; // Prop to trigger refresh from parent
}

export function RecentAnalysesWidget({ onViewAll, onHistoryClick, className = '', refreshTrigger }: RecentAnalysesWidgetProps) {
  const [analyses, setAnalyses] = useState<RecentAnalysisItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load recent analyses on component mount and when refreshTrigger changes
  useEffect(() => {
    loadRecentAnalyses();
  }, [refreshTrigger]);

  const loadRecentAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await RecentAnalysesService.getRecentAnalyses(4); // Get 4 most recent
      
      setAnalyses(data);
    } catch (err) {
      console.error('Error loading recent analyses:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load recent analyses';
      setError(errorMessage);
      setAnalyses([]); // Clear any previous data
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'rejected': return XCircle;
      default: return Clock;
    }
  };

  const handleAnalysisClick = (analysis: RecentAnalysisItem) => {
    if (onHistoryClick) {
      onHistoryClick(analysis);
    }
  };

  // Using the utility function from utils.ts

  return (
    <div className={`bg-white rounded-xl border border-gray-200 min-h-[500px] flex flex-col ${className}`}>
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Recent Analyses</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadRecentAnalyses}
              disabled={loading}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            {onViewAll && (
              <button
                onClick={onViewAll}
                className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center space-x-1 transition-colors"
              >
                <span>View all</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="p-6 flex flex-col flex-1">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-purple-600 animate-spin" />
            <span className="ml-2 text-gray-600">Loading analyses...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-8">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={loadRecentAnalyses}
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && analyses.length === 0 && (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-600 mb-2">No analyses yet</p>
            <p className="text-sm text-gray-500">Upload your first creative to get started</p>
          </div>
        )}

        {!loading && !error && analyses.length > 0 && (
          <div className="space-y-6 flex-1">
            {analyses.map((analysis, index) => {
              const StatusIcon = getStatusIcon(analysis.status);
              
              return (
                <div
                  key={analysis.id}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer group"
                  onClick={() => handleAnalysisClick(analysis)}
                >
                  <div className="relative">
                    <SecureImage
                      src={analysis.thumbnail}
                      alt={analysis.creativeName}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="absolute -top-1 -right-1">
                      {analysis.creativeType === 'video' ? (
                        <FileVideo className="w-4 h-4 text-purple-600" />
                      ) : (
                        <FileImage className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors" title={analysis.creativeName}>
                        {truncateAnalysisName.widget(analysis.creativeName)}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(analysis.status)}`}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {analysis.status}
                        </span>
                        <span className="font-bold text-gray-900">{analysis.overallScore}%</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-3">
                        <span>{analysis.version}</span>
                        <span>{formatRelativeDate(analysis.analysisDate)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CreditCard className="w-3 h-3" />
                        <span>{analysis.creditsUsed}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}